import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { useParams } from 'react-router-dom';
import { db } from '../../firebase';
import styleClasses from './css/Activity.module.scss';

import Loading from '../Loading';

const DEFAULT_IMAGE = '/assets/sample/activity_sample.png';

const Activity = () => {
  const { id: musicianId }              = useParams(); // URL에서 musicianId 가져오기
  const [selectedTab, setSelectedTab]   = useState('performance'); // 'performance' 또는 'contest'
  const [performances, setPerformances] = useState([]);
  const [contests, setContests]         = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isLoading, setIsLoading]       = useState(true);

  // Firestore에서 데이터를 가져오는 함수
  const fetchData = useCallback(async (collectionName, setData) => {
    try {
      const ref = collection(db, 'musicians', musicianId, collectionName);
      const snapshot = await getDocs(ref);

      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        date: new Date(doc.data().date), // date 필드를 Date 객체로 변환
      }));

      // 최신순으로 정렬
      const sortedData = data.sort((a, b) => b.date - a.date);

      setData(sortedData);

      // 첫 번째 항목을 기본 선택
      // if (!selectedItem && data.length > 0) {
      //   setSelectedItem(data[0]);
      // }
      return sortedData; // 데이터 로드 후에 어떤 데이터를 넣을 건지 지정 (데이터 여러 번 호출되는 이슈로 수정) - useEffect에서 데이터 가공
    } catch (error) {
      console.error(`Error fetching ${collectionName}:`, error);
    }
  }, [musicianId]);

  useEffect(() => {
    if (!musicianId) return;

    setIsLoading(true);

    const fetchAllData = async () => {
      try {
        const fetchedPerformances = await fetchData('stages', setPerformances);
        const fetchedContests     = await fetchData('events', setContests);

        if (fetchedPerformances.length > 0) {
          setSelectedItem(fetchedPerformances[0]);
        } else if (fetchedContests.length > 0) {
          setSelectedItem(fetchedContests[0]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, [musicianId, fetchData]);

  const activeList = (selectedTab === 'performance' && performances.length) ? performances : contests;

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    const firstItem = tab === 'performance' && performances.length > 0
      ? performances[0]
      : contests.length > 0
      ? contests[0]
      : null;

    setSelectedItem(firstItem); // 탭을 변경하면 첫 번째 항목 선택
  };

  return (
    isLoading
    ?
    <Loading />
    :
    <div className={styleClasses.activity}>
      {performances.length === 0 && contests.length === 0 ? (
        <p className={styleClasses.noData}>No data available yet. :(</p>
      ) : (
        <div className={styleClasses.activityContent}>
          {/* 왼쪽: 영상 및 사진 */}
          <div className={styleClasses.activityDisplay}>
            {selectedItem?.videoId ? (
              <iframe
                src={`https://www.youtube.com/embed/${selectedItem.videoId}`}
                title={selectedItem.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className={styleClasses.performanceVideo}
              ></iframe>
            ) : (
              <img
                src={selectedItem?.photo || DEFAULT_IMAGE}
                alt={selectedItem?.title}
                className={styleClasses.performanceThumbnail}
              />
            )}
            <h3 className={styleClasses.performanceTitle}>{selectedItem?.title}</h3>
            <p className={styleClasses.performanceStage}>{selectedItem?.stage || selectedItem?.host}</p>
            {selectedItem?.description && (
              <p className={styleClasses.performanceDescription}>{selectedItem.description}</p>
            )}
          </div>
  
          {/* 오른쪽: 공연/공모전 리스트 */}
          <div className={styleClasses.activityList}>
            {/* 탭 */}
            <div className={styleClasses.listTabs}>
              {performances.length > 0 && (
                <button
                  className={`${styleClasses.tab} ${ contests.length === 0 ? styleClasses.oneTab : '' } ${selectedTab === 'performance' && styleClasses.active}`}
                  onClick={() => handleTabChange('performance')}
                >
                  Performance List
                </button>
              )}
              {contests.length > 0 && (
                <button
                  className={`${styleClasses.tab} ${ performances.length === 0 ? styleClasses.oneTab : '' }  ${(selectedTab === 'contest' || performances.length === 0) && styleClasses.active}`}
                  onClick={() => handleTabChange('contest')}
                >
                  Event List
                </button>
              )}
            </div>
  
            {/* 리스트 */}
            {activeList.length > 0 ? (
              <ul className={styleClasses.performanceList}>
                {activeList.map((item) => (
                  <li
                    key={item.id}
                    className={`${styleClasses.listItem} ${
                      item.id === selectedItem?.id && styleClasses.active
                    }`}
                    onClick={() => setSelectedItem(item)}
                  >
                    <span className={styleClasses.listItemTitle}>{item.title}</span>
                    <span className={styleClasses.listItemDate}>
                      {item.date.toLocaleDateString()}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className={styleClasses.noData}>No data available yet. :(</p>
            )}
          </div>
        </div>
      )}
    </div>
  );  
};

export default Activity;
